import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, Users, Briefcase, Clock, Truck, BarChart3, MapPin,
  Calendar, AlertCircle, ChevronRight, Bell, Play, FileText,
  Edit3, Save, Shield, Square, User, MessageCircle, X
} from "lucide-react";
import { Button, Card, Badge, Spinner, Modal, Input, Select } from "../shared/ui";
import { useAuth } from "../App";
import PrestartPrompt from "../components/PrestartPrompt";
import QuickContactModal from "../components/QuickContactModal";
import LiveActivityDetail from "../components/LiveActivityDetail";
import { MachineConflictModal, parseMachineConflict } from "../components/MachineConflictModal";
import { MissedWeeksTile } from "../components/dashboard/MissedWeeksTile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockStatus, setClockStatus] = useState(null);
  const [clockedInUsers, setClockedInUsers] = useState([]);
  const [showForceClockOut, setShowForceClockOut] = useState(null); // user_id to clock out
  const [forceClockOutReason, setForceClockOutReason] = useState("forgot_to_clock_out");
  const [forceClockOutNotes, setForceClockOutNotes] = useState("");
  const [forceClockOutTime, setForceClockOutTime] = useState(""); // For backdated clock out
  const [showBackdateClockOut, setShowBackdateClockOut] = useState(false);
  const [backdateClockOutTime, setBackdateClockOutTime] = useState("");
  const [certSummary, setCertSummary] = useState(null);
  
  // Edit active worker state
  const [editingActiveWorker, setEditingActiveWorker] = useState(null);
  const [editActiveWorkerData, setEditActiveWorkerData] = useState({ clock_in: "", job_id: "", notes: "" });
  const [jobs, setJobs] = useState([]);

  const [pendingAcksCount, setPendingAcksCount] = useState(0);
  const [upcomingHires, setUpcomingHires] = useState([]);
  const [liveStatus, setLiveStatus] = useState(null);
  const [contactTarget, setContactTarget] = useState(null);
  const [machineConflict, setMachineConflict] = useState(null);
  const [pendingHireId, setPendingHireId] = useState(null);

  const canContactWorkers = ['admin', 'manager', 'supervisor', 'office_staff'].includes(user?.role);

  const isAdmin = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'office_staff';
  const isManager = user?.role === 'manager';
  const isSupervisor = user?.role === 'supervisor';
  const isWorkerOrSupervisor = ['supervisor', 'crane_operator', 'rigger', 'labourer'].includes(user?.role);
  const canViewClockedIn = isAdmin || isManager; // Admin/manager only — backend rejects others
  const canForceClockOut = user?.role === 'admin' || user?.role === 'manager';
  const canEditActiveWorker = ['admin', 'manager', 'office_staff'].includes(user?.role);
  const canViewCertificates = ['admin', 'manager', 'office_staff'].includes(user?.role);
  const canViewAllStats = ['admin', 'manager', 'office_staff'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const requests = [
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/time/status`)
      ];
      
      // Fetch clocked-in users for admins/supervisors
      if (canViewClockedIn) {
        requests.push(axios.get(`${API}/dashboard/clocked-in-users`));
      }

      // Fetch certificate summary for admins/managers
      if (canViewCertificates) {
        requests.push(axios.get(`${API}/certificates/dashboard-summary`));
      }
      
      const results = await Promise.all(requests);
      setStats(results[0].data);
      setClockStatus(results[1].data);
      
      let idx = 2;
      if (canViewClockedIn && results[idx]) {
        setClockedInUsers(results[idx].data);
        idx++;
      }
      if (canViewCertificates && results[idx]) {
        setCertSummary(results[idx].data);
      }
      
      // Fetch jobs for edit modal
      if (canEditActiveWorker) {
        const jobsRes = await axios.get(`${API}/jobs`);
        setJobs(jobsRes.data);
      }

      // Pending acknowledgements (everyone — backend filters by current_user.id)
      try {
        const ackRes = await axios.get(`${API}/forms/pending-acknowledgements/count`);
        setPendingAcksCount(ackRes.data.count || 0);
      } catch (e) { /* silent */ }

      // Upcoming scheduled general hire jobs for the current user (operator/dogman)
      try {
        const hiresRes = await axios.get(`${API}/general-hires/upcoming/for-user?days=14`);
        setUpcomingHires(hiresRes.data || []);
      } catch (e) { /* silent */ }

      // Live status — admin/office/manager only
      if (['admin', 'office_staff', 'manager'].includes(user?.role)) {
        try {
          const lsRes = await axios.get(`${API}/dashboard/live-status`);
          setLiveStatus(lsRes.data);
        } catch (e) { /* silent */ }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const clockOnScheduledHire = async (hireId) => {
    try {
      await axios.post(`${API}/general-hires/${hireId}/clock-on`, {});
      const { toast } = await import('sonner');
      toast.success("Clocked on to scheduled hire");
      fetchData();
      navigate('/time');
    } catch (e) {
      const conflict = parseMachineConflict(e);
      if (conflict) {
        setPendingHireId(hireId);
        setMachineConflict(conflict);
        return;
      }
      const { toast } = await import('sonner');
      toast.error(e.response?.data?.detail || "Failed to clock on");
    }
  };

  const handleClockAction = async (action) => {
    try {
      await axios.post(`${API}/time/${action}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || `${action} failed`);
    }
  };

  const handleEditActiveWorker = async () => {
    if (!editingActiveWorker) return;
    try {
      // Find the time entry ID for this user
      const entryRes = await axios.get(`${API}/time/all-entries?user_id=${editingActiveWorker.user_id}`);
      const activeEntry = entryRes.data.find(e => !e.clock_out);
      
      if (!activeEntry) {
        toast.error("Could not find active time entry for this user");
        return;
      }
      
      await axios.put(`${API}/time/entries/${activeEntry.id}`, {
        clock_in: editActiveWorkerData.clock_in || null,
        job_id: editActiveWorkerData.job_id || null,
        notes: editActiveWorkerData.notes || null
      });
      
      toast.success("Worker time entry updated!");
      setEditingActiveWorker(null);
      setEditActiveWorkerData({ clock_in: "", job_id: "", notes: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update worker");
    }
  };

  const openEditActiveWorker = (entry) => {
    setEditingActiveWorker(entry);
    setEditActiveWorkerData({
      clock_in: entry.clock_in_time?.slice(0, 16) || "",
      job_id: entry.job_id || "",
      notes: ""
    });
  };

  const handleForceClockOut = async (userId) => {
    try {
      const payload = {
        user_id: userId,
        reason: forceClockOutReason,
        notes: forceClockOutNotes || null
      };
      // Add clock_out_time if backdating for "forgot to clock out"
      if (forceClockOutReason === "forgot_to_clock_out" && forceClockOutTime) {
        payload.clock_out_time = new Date(forceClockOutTime).toISOString();
      }
      await axios.post(`${API}/time/force-clock-out`, payload);
      toast.success("User clocked out successfully");
      setShowForceClockOut(null);
      setForceClockOutReason("forgot_to_clock_out");
      setForceClockOutNotes("");
      setForceClockOutTime("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clock out user");
    }
  };

  const handleSelfClockOut = async () => {
    try {
      const payload = {};
      if (showBackdateClockOut && backdateClockOutTime) {
        payload.clock_out_time = new Date(backdateClockOutTime).toISOString();
      }
      await axios.post(`${API}/time/clock-out`, payload);
      setShowBackdateClockOut(false);
      setBackdateClockOutTime("");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clock out failed");
    }
  };

  // Admin/Manager/Office: delete anything on the dashboard
  const deleteScheduledHire = async (hireId, machineName) => {
    if (!window.confirm(`Delete this scheduled hire (${machineName || 'hire'})? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/general-hires/${hireId}`);
      toast.success("Scheduled hire deleted");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete hire");
    }
  };

  const deleteCertificate = async (cert) => {
    if (!window.confirm(`Delete certificate "${cert.name}" for ${cert.user_name}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${cert.user_id}/certificates/${cert.id}`);
      toast.success("Certificate deleted");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete certificate");
    }
  };

  const deleteClockedInEntry = async (entry) => {
    if (!window.confirm(`WIPE clock-in entry for ${entry.user_name}?\n\nThis fully deletes the time entry from payroll. Use "Clock Out" instead if it's a real worker who just forgot.\n\nOnly use this to remove test/junk entries.`)) return;
    try {
      const entryRes = await axios.get(`${API}/time/all-entries?user_id=${entry.user_id}`);
      const activeEntry = entryRes.data.find(e => !e.clock_out);
      if (!activeEntry) {
        toast.error("Could not find active time entry");
        return;
      }
      await axios.delete(`${API}/time/entries/${activeEntry.id}`);
      toast.success("Clock-in entry deleted");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete entry");
    }
  };

  const CLOCK_OUT_REASONS = [
    { value: "forgot_to_clock_out", label: "Forgot to clock out" },
    { value: "end_of_shift", label: "End of shift" },
    { value: "left_site_early", label: "Left site early" },
    { value: "admin_correction", label: "Admin correction" },
    { value: "other", label: "Other" }
  ];

  const formatClockInTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
  };

  const getHoursOnSite = (clockInTime) => {
    const start = new Date(clockInTime);
    const now = new Date();
    const diffMs = now - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Pending acknowledgements banner — shown to anyone with forms waiting on their signature */}
      {pendingAcksCount > 0 && (
        <Card
          interactive
          onClick={() => navigate('/forms?tab=acknowledgements')}
          className="bg-amber-50 border-2 border-amber-300 hover:bg-amber-100"
          data-testid="pending-acks-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Edit3 className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold uppercase text-amber-900">
                {pendingAcksCount} form{pendingAcksCount === 1 ? '' : 's'} need{pendingAcksCount === 1 ? 's' : ''} your signature
              </p>
              <p className="text-sm text-amber-700">
                A supervisor has filled in {pendingAcksCount === 1 ? 'a form' : `${pendingAcksCount} forms`} — read through and sign to acknowledge.
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-amber-600 flex-shrink-0" />
          </div>
        </Card>
      )}

      {/* Daily Machine Prestart Prompt — only shows if user has open machine usage today */}
      <PrestartPrompt />

      {/* Upcoming scheduled general hire jobs — operator/dogman fast clock-on */}
      {upcomingHires.length > 0 && (
        <Card className="border-2 border-blue-300 bg-blue-50" data-testid="upcoming-hires-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-bold uppercase text-blue-900">Your Scheduled General Hire Jobs</h3>
              <p className="text-xs text-blue-700">Upcoming one-off hires where you're the operator or dogman</p>
            </div>
            <Badge variant="info">{upcomingHires.length}</Badge>
          </div>
          <div className="space-y-2">
            {upcomingHires.slice(0, 5).map(h => {
              const today = new Date().toISOString().split('T')[0];
              const isToday = h.work_date === today;
              return (
                <div key={h.id} className="bg-white rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`scheduled-hire-item-${h.id}`}>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold">{h.machine_name}</p>
                      {isToday && <Badge variant="success">Today</Badge>}
                      <Badge variant={h.status === 'active' ? 'warning' : 'info'}>{h.status}</Badge>
                    </div>
                    <p className="text-xs text-slate-600">
                      <strong>{h.client_name || 'Client TBC'}</strong>
                      {h.location ? ` · ${h.location}` : ''}
                      {h.start_time ? ` · ${h.start_time}` : ''}
                    </p>
                    <p className="text-xs text-slate-500">
                      {new Date(h.work_date).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
                      {h.operator_id === user.id ? ' · You: Operator' : (h.dogman_ids?.includes(user.id) ? ' · You: Dogman' : '')}
                    </p>
                  </div>
                  {isToday && h.operator_id === user.id && !h.clock_on_time && (
                    <Button
                      size="sm"
                      onClick={() => clockOnScheduledHire(h.id)}
                      data-testid={`clock-on-hire-${h.id}`}
                    >
                      Clock On
                    </Button>
                  )}
                  {isAdmin && (
                    <button
                      onClick={() => deleteScheduledHire(h.id, h.machine_name)}
                      className="p-1.5 rounded-md bg-red-100 hover:bg-red-200 text-red-700"
                      title="Delete this scheduled hire"
                      data-testid={`delete-hire-${h.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight font-heading">
            G'day, {user?.first_name}!
          </h1>
          <p className="text-slate-600 mt-1">Here's what's happening today</p>
        </div>

        {/* Go to Time Page Button - Large and easy to tap on mobile */}
        {!isAdmin && (
          <div className="w-full md:w-auto">
            <Button
              onClick={() => navigate('/time')}
              variant={clockStatus?.clocked_in ? "destructive" : "default"}
              data-testid="go-to-time-btn"
              className="w-full md:w-auto text-lg md:text-base py-4 md:py-2 px-6 rounded-xl font-bold touch-manipulation"
              style={{ minHeight: '56px' }}
            >
              <Clock className="w-6 h-6 mr-2" />
              {clockStatus?.clocked_in ? "Go to Clock Out" : "Go to Clock In"}
            </Button>
          </div>
        )}
        
        {/* Backdate Clock Out Modal */}
        {showBackdateClockOut && (
          <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 overflow-y-auto p-4 pb-32 sm:pb-4" onClick={() => setShowBackdateClockOut(false)}>
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mt-12 sm:mt-0" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold mb-4">Forgot to Clock Out?</h3>
              <p className="text-sm text-slate-600 mb-4">Enter the time you actually finished work:</p>
              <input
                type="datetime-local"
                value={backdateClockOutTime}
                onChange={(e) => setBackdateClockOutTime(e.target.value)}
                className="w-full h-12 bg-white border-2 border-slate-200 focus:border-orange-500 focus:outline-none rounded-md px-3"
              />
              <div className="flex gap-3 mt-6">
                <Button onClick={handleSelfClockOut}>
                  Clock Out
                </Button>
                <Button variant="ghost" onClick={() => setShowBackdateClockOut(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {isAdmin ? (
          <>
            <StatCard label="Total Employees" value={stats?.total_users || 0} icon={Users} />
            <StatCard label="Active Jobs" value={stats?.active_jobs || 0} icon={Briefcase} color="orange" />
            <StatCard label="Machines" value={stats?.total_machines || 0} icon={Truck} color="blue" />
            <StatCard label="Clocked In" value={stats?.users_clocked_in || 0} icon={Clock} color="green" />
          </>
        ) : isSupervisor ? (
          <>
            <StatCard label="My Jobs" value={stats?.my_jobs || 0} icon={Briefcase} color="orange" />
            <StatCard label="Team Size" value={stats?.team_size || 0} icon={Users} />
            <StatCard label="Reports" value={stats?.pending_reports || 0} icon={BarChart3} color="blue" />
            <StatCard 
              label="Status" 
              value={clockStatus?.clocked_in ? "Clocked In" : "Clocked Out"} 
              icon={Clock} 
              color={clockStatus?.clocked_in ? "green" : "default"} 
            />
          </>
        ) : (
          <>
            <StatCard label="My Jobs" value={stats?.my_jobs || 0} icon={Briefcase} color="orange" />
            <StatCard label="Hours This Week" value={stats?.hours_this_week || 0} icon={Clock} color="green" />
            <StatCard label="Forms Submitted" value={stats?.forms_submitted || 0} icon={FileText} color="blue" />
            <StatCard 
              label="Status" 
              value={clockStatus?.clocked_in ? "Clocked In" : "Clocked Out"} 
              icon={Clock} 
              color={clockStatus?.clocked_in ? "green" : "default"} 
            />
          </>
        )}
      </div>

      {/* Missed-Weeks tile — visible only to approvers (component renders null otherwise) */}
      {(isAdmin || isSupervisor) && <MissedWeeksTile />}

      {/* Quick Actions */}
      <Card>
        <h3 className="text-xl font-bold uppercase tracking-tight font-heading mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <QuickActionButton icon={Briefcase} label="View Jobs" path="/jobs" />
          <QuickActionButton icon={Truck} label="Machines" path="/machines" />
          <QuickActionButton icon={FileText} label="Fill Form" path="/forms" />
          <QuickActionButton icon={Clock} label="Time Log" path="/time" />
          <QuickActionButton icon={Bell} label="Notifications" path="/notifications" />
        </div>
      </Card>

      {/* Live Activity totals strip */}
      {canViewClockedIn && liveStatus && (
        <Card data-testid="live-status-section" className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xl font-bold uppercase tracking-tight font-heading flex items-center gap-2">
              <Truck className="w-6 h-6 text-blue-600" />
              Live Activity
            </h3>
            <span className="text-xs text-slate-500">{liveStatus.total_active_workers} worker{liveStatus.total_active_workers === 1 ? '' : 's'} active</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-green-50 border border-green-200 rounded-lg p-2 text-center" data-testid="live-stat-clocked_in">
              <p className="text-2xl font-bold text-green-700">{liveStatus.totals?.clocked_in || 0}</p>
              <p className="text-[10px] uppercase font-bold text-green-600">Clocked In</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-center" data-testid="live-stat-travelling">
              <p className="text-2xl font-bold text-amber-700">{liveStatus.totals?.travelling || 0}</p>
              <p className="text-[10px] uppercase font-bold text-amber-600">Travelling</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-2 text-center" data-testid="live-stat-on_machine">
              <p className="text-2xl font-bold text-purple-700">{liveStatus.totals?.on_machine || 0}</p>
              <p className="text-[10px] uppercase font-bold text-purple-600">On Machine</p>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-2 text-center" data-testid="live-stat-general_hire">
              <p className="text-2xl font-bold text-rose-700">{liveStatus.totals?.general_hire || 0}</p>
              <p className="text-[10px] uppercase font-bold text-rose-600">General Hire</p>
            </div>
          </div>
        </Card>
      )}

      {/* Detailed Travelling + On Machine + General Hire sections */}
      {canViewClockedIn && liveStatus && (
        <LiveActivityDetail
          liveStatus={liveStatus}
          onContact={(row) => setContactTarget(row)}
          refresh={fetchData}
        />
      )}

      {/* Who's On Site - existing detail breakdown */}
      {canViewClockedIn && (
        <Card data-testid="clocked-in-section" className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold uppercase tracking-tight font-heading flex items-center gap-2">
              <MapPin className="w-6 h-6 text-green-600" />
              {isWorkerOrSupervisor && clockStatus?.clocked_in ? "Who's On My Site" : "Who's On Site Now"}
            </h3>
            <div className="flex items-center gap-3">
              {(() => {
                const visibleUsers = isWorkerOrSupervisor && clockStatus?.clocked_in
                  ? clockedInUsers.filter(entry => entry.job_id === clockStatus?.job_id)
                  : clockedInUsers;
                return <Badge variant="success" className="text-lg px-4 py-1">{visibleUsers.length} Active</Badge>;
              })()}
              {canViewAllStats && (
                <Button size="sm" variant="outline" onClick={() => navigate('/analytics')}>
                  <BarChart3 className="w-4 h-4 mr-1" /> Full View
                </Button>
              )}
            </div>
          </div>
          
          {(() => {
            const visibleUsers = isWorkerOrSupervisor && clockStatus?.clocked_in
              ? clockedInUsers.filter(entry => entry.job_id === clockStatus?.job_id)
              : isWorkerOrSupervisor && !clockStatus?.clocked_in
                ? []
                : clockedInUsers;
            
            // Group by job site for easier viewing
            const byJob = {};
            visibleUsers.forEach(entry => {
              const jobKey = entry.job_title || 'No Job Assigned';
              if (!byJob[jobKey]) {
                byJob[jobKey] = { location: entry.job_location, workers: [] };
              }
              byJob[jobKey].workers.push(entry);
            });
            
            return visibleUsers.length > 0 ? (
              <div className="space-y-4">
                {Object.entries(byJob).map(([jobTitle, jobData]) => (
                  <div key={jobTitle} className="bg-white rounded-lg border-2 border-slate-100 overflow-hidden">
                    <div
                      className={`bg-slate-100 px-4 py-2 flex items-center justify-between ${(!isWorkerOrSupervisor && jobData.workers[0]?.job_id) ? 'cursor-pointer hover:bg-slate-200 transition-colors' : ''}`}
                      onClick={() => {
                        if (!isWorkerOrSupervisor && jobData.workers[0]?.job_id) {
                          navigate(`/jobs?open=${jobData.workers[0].job_id}`);
                        }
                      }}
                      data-testid={`dashboard-job-header-${jobData.workers[0]?.job_id || 'none'}`}
                    >
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-orange-500" />
                        <span className="font-bold text-slate-800">{jobTitle}</span>
                        {jobData.location && (
                          <span className="text-xs text-slate-500">• {jobData.location}</span>
                        )}
                      </div>
                      <Badge variant="info">{jobData.workers.length} worker{jobData.workers.length !== 1 ? 's' : ''}</Badge>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {jobData.workers.map((entry, idx) => (
                        <div 
                          key={idx} 
                          className="flex flex-col md:flex-row md:items-center justify-between p-3 hover:bg-green-50 transition-colors gap-2"
                          data-testid={`clocked-in-user-${entry.user_id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                              <User className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">{entry.user_name}</p>
                              <p className="text-sm text-slate-500 capitalize">{entry.user_role?.replace('_', ' ')}</p>
                            </div>
                          </div>
                          
                          {/* Show machine if assigned */}
                          {entry.machine_name && (
                            <div className="flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
                              <Truck className="w-4 h-4 text-blue-500" />
                              <span className="text-sm font-medium text-blue-700">{entry.machine_name}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-3">
                            <div className="text-left md:text-right">
                              <p className="text-sm text-green-600 font-medium">
                                Since {formatClockInTime(entry.clock_in_time)}
                              </p>
                              <p className="text-xs text-slate-400">
                                {getHoursOnSite(entry.clock_in_time)} on site
                              </p>
                            </div>
                            {canEditActiveWorker && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openEditActiveWorker(entry)}
                                title="Edit clock in time and job"
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                            )}
                            {canForceClockOut && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setShowForceClockOut(entry.user_id)}
                                data-testid={`force-clock-out-${entry.user_id}`}
                                title="Clock out this user"
                              >
                                <Square className="w-4 h-4" />
                              </Button>
                            )}
                            {canForceClockOut && (
                              <button
                                onClick={() => deleteClockedInEntry(entry)}
                                className="p-2 rounded-md bg-red-100 hover:bg-red-200 text-red-700"
                                title="Delete this clock-in entry (removes from payroll)"
                                data-testid={`delete-clocked-in-${entry.user_id}`}
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          
                          {/* Force Clock Out Modal */}
                          {showForceClockOut === entry.user_id && (
                            <div className="fixed inset-0 bg-black/50 flex items-start sm:items-center justify-center z-50 overflow-y-auto p-4 pb-32 sm:pb-4" onClick={() => setShowForceClockOut(null)}>
                              <div className="bg-white rounded-lg p-6 max-w-md w-full mt-12 sm:mt-0" onClick={(e) => e.stopPropagation()}>
                                <h3 className="text-lg font-bold mb-4">Clock Out {entry.user_name}</h3>
                                <Select
                                  label="Reason"
                                  value={forceClockOutReason}
                                  onChange={(e) => setForceClockOutReason(e.target.value)}
                                  options={CLOCK_OUT_REASONS}
                                />
                                {forceClockOutReason === "forgot_to_clock_out" && (
                                  <div className="mt-4">
                                    <label className="block text-sm font-bold uppercase text-slate-600 mb-1">Actual Clock Out Time</label>
                                    <input
                                      type="datetime-local"
                                      value={forceClockOutTime}
                                      onChange={(e) => setForceClockOutTime(e.target.value)}
                                      data-testid="force-clock-out-time-input"
                                      className="w-full h-14 bg-white border-2 border-slate-300 focus:border-orange-500 focus:outline-none rounded-md px-4 text-lg font-mono tracking-wide"
                                    />
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      <button type="button"
                                        onClick={() => {
                                          const d = new Date(); d.setHours(d.getHours() - 1);
                                          d.setSeconds(0); d.setMilliseconds(0);
                                          setForceClockOutTime(d.toISOString().slice(0, 16));
                                        }}
                                        className="px-3 py-1.5 text-xs font-bold uppercase bg-slate-100 hover:bg-slate-200 rounded"
                                        data-testid="qt-1h-ago"
                                      >1 hr ago</button>
                                      <button type="button"
                                        onClick={() => {
                                          const d = new Date(); d.setHours(17, 0, 0, 0);
                                          if (d > new Date()) d.setDate(d.getDate() - 1);
                                          setForceClockOutTime(d.toISOString().slice(0, 16));
                                        }}
                                        className="px-3 py-1.5 text-xs font-bold uppercase bg-slate-100 hover:bg-slate-200 rounded"
                                        data-testid="qt-end-of-day"
                                      >5pm today</button>
                                      <button type="button"
                                        onClick={() => setForceClockOutTime("")}
                                        className="px-3 py-1.5 text-xs font-bold uppercase bg-slate-100 hover:bg-slate-200 rounded"
                                        data-testid="qt-clear"
                                      >Now</button>
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2">Tap a quick-time button or type the time directly. Leave empty to use the current time.</p>
                                  </div>
                                )}
                                <div className="mt-4">
                                  <Input
                                    label="Notes (optional)"
                                    value={forceClockOutNotes}
                                    onChange={(e) => setForceClockOutNotes(e.target.value)}
                                    placeholder="Additional notes..."
                                  />
                                </div>
                                <div className="flex gap-3 mt-6">
                                  <Button onClick={() => handleForceClockOut(entry.user_id)}>
                                    Clock Out
                                  </Button>
                                  <Button variant="ghost" onClick={() => setShowForceClockOut(null)}>
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium">
                {isWorkerOrSupervisor && !clockStatus?.clocked_in 
                  ? "Clock in to see who's on your site" 
                  : "No one clocked in right now"}
              </p>
              <p className="text-sm">Workers will appear here when they clock in</p>
            </div>
          );
          })()}
        </Card>
      )}

      {/* Edit Active Worker Modal */}
      {editingActiveWorker && (
        <Modal isOpen={true} onClose={() => setEditingActiveWorker(null)} title={`Edit ${editingActiveWorker.user_name}'s Time Entry`} size="lg">
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="font-bold text-green-800">{editingActiveWorker.user_name}</p>
              <p className="text-sm text-green-600">Currently at: {editingActiveWorker.job_title}</p>
            </div>
            
            <div>
              <label className="block text-sm font-bold uppercase text-slate-600 mb-1">Clock In Time</label>
              <input
                type="datetime-local"
                value={editActiveWorkerData.clock_in}
                onChange={(e) => setEditActiveWorkerData({...editActiveWorkerData, clock_in: e.target.value})}
                className="w-full h-12 border-2 border-slate-200 rounded-md px-3"
              />
              <p className="text-xs text-slate-500 mt-1">Change the time this worker clocked in</p>
            </div>
            
            <Select
              label="Change Job Site"
              value={editActiveWorkerData.job_id}
              onChange={(e) => setEditActiveWorkerData({...editActiveWorkerData, job_id: e.target.value})}
              options={[
                { value: "", label: "-- Keep Current Job --" },
                ...jobs.map(j => ({ value: j.id, label: j.title }))
              ]}
            />
            
            <Input
              label="Reason for Change"
              value={editActiveWorkerData.notes}
              onChange={(e) => setEditActiveWorkerData({...editActiveWorkerData, notes: e.target.value})}
              placeholder="Why are you making this change?"
            />
            
            <div className="flex gap-3 pt-4 sticky bottom-0 -mx-4 sm:-mx-6 px-4 sm:px-6 pb-4 sm:pb-0 bg-white border-t border-slate-100 mt-2">
              <Button onClick={handleEditActiveWorker} data-testid="edit-active-worker-save">
                <Save className="w-4 h-4 mr-2" /> Save Changes
              </Button>
              <Button variant="ghost" onClick={() => setEditingActiveWorker(null)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Certificate Expiry Alerts - visible to admin/manager/office_staff */}
      {canViewCertificates && certSummary && (certSummary.expired_count > 0 || certSummary.expiring_30_count > 0) && (
        <Card data-testid="certificate-alerts-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold uppercase tracking-tight font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Certificate Alerts
            </h3>
            <Badge variant={certSummary.expired_count > 0 ? "error" : "warning"}>
              {certSummary.total_alerts} Action{certSummary.total_alerts !== 1 ? 's' : ''} Needed
            </Badge>
          </div>
          
          {/* Expired Certificates */}
          {certSummary.expired_count > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-bold uppercase text-red-600 mb-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                Expired ({certSummary.expired_count})
              </h4>
              <div className="space-y-2">
                {certSummary.expired.slice(0, 5).map((cert, idx) => (
                  <div 
                    key={idx}
                    onClick={() => navigate(`/employees?highlight=${cert.user_id}`)}
                    className="flex items-center justify-between p-3 bg-red-50 border-2 border-red-200 rounded-lg cursor-pointer hover:border-red-400 transition-colors"
                    data-testid={`expired-cert-${cert.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-red-800">{cert.name}</p>
                        <p className="text-sm text-red-600">{cert.user_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <Badge variant="error">EXPIRED</Badge>
                        <p className="text-xs text-red-600 mt-1">{new Date(cert.expiry_date).toLocaleDateString()}</p>
                      </div>
                      {isAdmin && (
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteCertificate(cert); }}
                          className="p-1.5 rounded-md bg-red-200 hover:bg-red-300 text-red-800"
                          title="Delete this certificate"
                          data-testid={`delete-expired-cert-${cert.id}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expiring Soon (30 days) */}
          {certSummary.expiring_30_count > 0 && (
            <div>
              <h4 className="text-sm font-bold uppercase text-amber-600 mb-2 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Expiring Soon ({certSummary.expiring_30_count})
              </h4>
              <div className="space-y-2">
                {certSummary.expiring_30_days.slice(0, 5).map((cert, idx) => {
                  const daysLeft = Math.ceil((new Date(cert.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
                  return (
                    <div 
                      key={idx}
                      onClick={() => navigate(`/employees?highlight=${cert.user_id}`)}
                      className="flex items-center justify-between p-3 bg-amber-50 border-2 border-amber-200 rounded-lg cursor-pointer hover:border-amber-400 transition-colors"
                      data-testid={`expiring-cert-${cert.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                          <Clock className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-amber-800">{cert.name}</p>
                          <p className="text-sm text-amber-600">{cert.user_name}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <Badge variant="warning">{daysLeft} days</Badge>
                          <p className="text-xs text-amber-600 mt-1">{new Date(cert.expiry_date).toLocaleDateString()}</p>
                        </div>
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteCertificate(cert); }}
                            className="p-1.5 rounded-md bg-amber-200 hover:bg-amber-300 text-amber-800"
                            title="Delete this certificate"
                            data-testid={`delete-expiring-cert-${cert.id}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* View All Link */}
          <button
            onClick={() => navigate('/employees')}
            className="w-full mt-4 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
          >
            View All Employee Certificates →
          </button>
        </Card>
      )}

      {/* Quick contact modal — shared across Live Activity rows */}
      <QuickContactModal
        isOpen={!!contactTarget}
        onClose={() => setContactTarget(null)}
        target={contactTarget}
      />

      {/* Machine conflict resolver — pops when clocking onto a machine while still on another */}
      {machineConflict && (
        <MachineConflictModal
          conflict={machineConflict}
          onClose={() => { setMachineConflict(null); setPendingHireId(null); }}
          onResolved={() => {
            const hid = pendingHireId;
            setMachineConflict(null);
            setPendingHireId(null);
            if (hid) clockOnScheduledHire(hid);
            else fetchData();
          }}
        />
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color = "default" }) => {
  const colors = {
    default: "text-slate-600",
    orange: "text-orange-500",
    green: "text-green-600",
    blue: "text-blue-600",
    red: "text-red-600"
  };

  return (
    <Card className="flex flex-col">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold uppercase text-slate-500">{label}</span>
        <Icon className={`w-5 h-5 ${colors[color]}`} />
      </div>
      <span className={`text-3xl font-bold font-mono ${colors[color]}`}>{value}</span>
    </Card>
  );
};

const QuickActionButton = ({ icon: Icon, label, path }) => {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(path)}
      className="flex flex-col items-center justify-center p-4 border-2 border-slate-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all duration-200"
    >
      <Icon className="w-8 h-8 text-orange-500 mb-2" />
      <span className="text-sm font-semibold">{label}</span>
    </button>
  );
};



export default DashboardPage;
